import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Search, SlidersHorizontal, X } from "lucide-react";
import { billsRead, isFeatureOff } from "@/lib/billsNative";
import { useSession } from "@/contexts/SessionContext";
import { BillListCard } from "@/components/bills/BillListCard";

const TABS = [
  { id: "attention", label: "Needs attention" },
  { id: "drafts", label: "Drafts" },
  { id: "confirmed", label: "Confirmed" },
];
const EMPTY = {
  attention: "Nothing needs your attention. New bills that need a detail will show up here.",
  drafts: "No draft bills. Start a review from a captured bill to create one.",
  confirmed: "No confirmed bills yet in Bills.",
};

const Filters = ({ value, onChange, outlets }) => (
  <div className="mt-3 grid grid-cols-2 gap-2" data-testid="bills-filters">
    <label className="text-[12px] text-archival-secondary">From
      <input type="date" value={value.from} onChange={(e) => onChange({ ...value, from: e.target.value })} data-testid="bills-filter-from"
        className="mt-1 w-full h-11 rounded-md border border-[#E4E0D8] bg-white px-2 text-[15px]" /></label>
    <label className="text-[12px] text-archival-secondary">To
      <input type="date" value={value.to} onChange={(e) => onChange({ ...value, to: e.target.value })} data-testid="bills-filter-to"
        className="mt-1 w-full h-11 rounded-md border border-[#E4E0D8] bg-white px-2 text-[15px]" /></label>
    {outlets.length > 1 && (
      <label className="col-span-2 text-[12px] text-archival-secondary">Outlet
        <select value={value.outlet} onChange={(e) => onChange({ ...value, outlet: e.target.value })} data-testid="bills-filter-outlet"
          className="mt-1 w-full h-11 rounded-md border border-[#E4E0D8] bg-white px-2 text-[15px]">
          <option value="">All my outlets</option>
          {outlets.map((o) => <option key={o.id} value={o.id}>{o.name || o.id}</option>)}
        </select></label>
    )}
  </div>
);

export default function BillsHome() {
  const navigate = useNavigate();
  const { outlets } = useSession();
  const [params, setParams] = useSearchParams();
  const view = TABS.some((t) => t.id === params.get("view")) ? params.get("view") : "attention";
  const [q, setQ] = useState(params.get("q") || "");
  const [filters, setFilters] = useState({ from: "", to: "", outlet: "" });
  const [showFilters, setShowFilters] = useState(false);
  const [state, setState] = useState({ phase: "loading", items: [], counts: null, next: null });
  const [more, setMore] = useState(false);
  const gen = useRef(0);

  const query = useCallback((cursor) => ({ view, q: q.trim() || undefined, from: filters.from || undefined,
    to: filters.to || undefined, outlet: filters.outlet || undefined, cursor, limit: 20 }), [view, q, filters]);

  useEffect(() => {
    const g = ++gen.current;
    const t = setTimeout(async () => {  // debounce search typing; no request per keystroke
      setState((s) => ({ ...s, phase: s.items.length ? "refreshing" : "loading" }));
      const r = await billsRead.list(query());
      if (g !== gen.current) return;
      if (isFeatureOff(r)) return setState({ phase: "off", items: [], counts: null, next: null });
      if (!r.ok) return setState({ phase: r.status === 503 || r.status === 0 ? "unavailable" : "error", items: [], counts: null, next: null });
      setState({ phase: "ready", items: r.data.items, counts: r.data.counts, next: r.data.next_cursor });
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const loadMore = async () => {
    setMore(true);
    const r = await billsRead.list(query(state.next));
    setMore(false);
    if (r.ok) setState((s) => ({ ...s, items: [...s.items, ...r.data.items], next: r.data.next_cursor }));
  };

  const open = (b) => navigate(b.status === "CONFIRMED" ? `/bills/detail/${b.bill_id}` : `/bills/${b.candidate_id}`);
  const setView = (v) => setParams((p) => { p.set("view", v); return p; }, { replace: true });

  return (
    <div className="min-h-screen bg-archival-base" data-testid="bills-home">
      <div className="max-w-[640px] mx-auto px-4 pb-10">
        <div className="flex items-center justify-between pt-4">
          <h1 className="text-[24px] font-semibold text-archival-primary tracking-tight">Bills</h1>
          <Link to="/queue" className="text-[13px] text-archival-secondary underline underline-offset-2 min-h-[44px] inline-flex items-center" data-testid="bills-home-capture-link">Captured bills</Link>
        </div>
        <div role="tablist" aria-label="Bill lists" className="mt-3 grid grid-cols-3 gap-1 rounded-md bg-[#efece6] p-1">
          {TABS.map((t) => (
            <button key={t.id} role="tab" aria-selected={view === t.id} onClick={() => setView(t.id)} data-testid={`bills-tab-${t.id}`}
              className={`min-h-[44px] rounded px-1 text-[13px] font-medium leading-tight transition-colors ${view === t.id ? "bg-white text-archival-primary shadow-sm" : "text-archival-secondary"}`}>
              {t.label}
              {state.counts && <span className="ml-1 tabular-nums" data-testid={`bills-tab-count-${t.id}`}>({state.counts[t.id]})</span>}
            </button>
          ))}
        </div>
        <div className="mt-3 flex gap-2">
          <label className="relative flex-1">
            <span className="sr-only">Search bills</span>
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-archival-muted" aria-hidden />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search bill number or vendor" data-testid="bills-search-input"
              className="w-full h-12 rounded-md border border-[#E4E0D8] bg-white pl-9 pr-9 text-[16px] focus:outline-none focus:ring-2 focus:ring-[var(--action)]" />
            {q && <button type="button" onClick={() => setQ("")} aria-label="Clear search" data-testid="bills-search-clear"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-10 w-10 inline-flex items-center justify-center text-archival-muted"><X size={16} /></button>}
          </label>
          <button type="button" onClick={() => setShowFilters((v) => !v)} aria-expanded={showFilters} data-testid="bills-filters-toggle"
            className="h-12 w-12 rounded-md border border-[#E4E0D8] bg-white inline-flex items-center justify-center" aria-label="Filters"><SlidersHorizontal size={18} /></button>
        </div>
        {showFilters && <Filters value={filters} onChange={setFilters} outlets={outlets || []} />}
        <Body state={state} view={view} searching={!!q.trim() || !!filters.from || !!filters.to} onOpen={open} onMore={loadMore} more={more} />
      </div>
    </div>
  );
}

const Body = ({ state, view, searching, onOpen, onMore, more }) => {
  if (state.phase === "loading") return <p className="mt-8 flex items-center gap-2 text-[14px] text-archival-secondary" data-testid="bills-loading"><Loader2 size={16} className="animate-spin" />Loading bills…</p>;
  if (state.phase === "off") return <p className="mt-8 text-[15px] text-archival-secondary" data-testid="bills-off">Bills isn't turned on for this account yet.</p>;
  if (state.phase === "unavailable") return <p role="alert" className="mt-8 text-[15px] text-archival-secondary" data-testid="bills-unavailable">Bills is temporarily unavailable. Please try again shortly.</p>;
  if (state.phase === "error") return <p role="alert" className="mt-8 text-[15px] text-red-800" data-testid="bills-error">We couldn't load bills. Please try again.</p>;
  if (!state.items.length)
    return <p className="mt-8 text-[15px] text-archival-secondary" data-testid="bills-empty">{searching ? "No bills match your search." : EMPTY[view]}</p>;
  return (
    <>
      <ul className="mt-4 space-y-2" data-testid="bills-list" aria-busy={state.phase === "refreshing"}>
        {state.items.map((b) => <BillListCard key={b.bill_id} bill={b} onOpen={onOpen} />)}
      </ul>
      {state.next && (
        <button type="button" onClick={onMore} disabled={more} data-testid="bills-load-more"
          className="mt-4 w-full h-12 rounded-md border border-[#E4E0D8] bg-white text-[15px] font-medium inline-flex items-center justify-center gap-2">
          {more && <Loader2 size={16} className="animate-spin" />}Show more
        </button>
      )}
    </>
  );
};
